/**
 * Combat input.
 *
 * Mouse look through Pointer Lock, WASD footwork, and one verb: hold, speak,
 * draw, release. There is still no basic attack, no number keys and no parry
 * button. Holding freezes the camera and turns pointer movement into a sigil
 * stroke; releasing sends the evidence and the server works out which spell
 * that was.
 *
 * The camera stays locked during a gesture: `movementX/Y` keeps flowing while
 * locked, so the pointer can never escape the window mid-duel and the player
 * never has to re-acquire the lock between casts.
 *
 * Footwork is the second way to answer a shot — sidestep it instead of blocking
 * it — and it is a *bounded* one. The keys held here are sent as a direction and
 * nothing else; the room integrates them, and this file predicts the same walk
 * locally only so the camera answers the key immediately instead of a round trip
 * later. Where the prediction and the server disagree, the server wins.
 */
import { useEffect, useRef } from 'react';
import { C2S } from '@spell/network-protocol';
import { MOVEMENT } from '@spell/shared-types';
import { chantSimilarity, combineScores, stanceSpeed, stepStance } from '@spell/combat-rules';
import { scoreAttempt } from '@spell/gesture-engine';
import { chantSyllables, normalizeChant } from '@spell/spell-schema';
import { net, SpellNet } from '../networking/net';
import { useNet } from '../networking/netStore';
import { combatRecorder, gestureTrail, syncTrailFrom } from '../gesture/capture';
import { castState, lastAttempt, resetCast, useCastUi } from './castState';
import { scene as bus } from '../scene/sceneBus';
import { wandRig } from '../entities/PlayerWand';
import { chantRecognizer, mic } from '../voice/mic';
import { sfx } from '../audio/sfx';
import { useSettings } from './settingsStore';

const AIM_SEND_INTERVAL_MS = 50;

/**
 * The colour a circle opens in, before the live guess has an opinion.
 *
 * Neutral on purpose: a cast that started in some spell's colour and then
 * changed would read as the game changing its mind, where starting blank and
 * resolving reads as the sigil being recognised.
 */
const NEUTRAL_CAST_COLOR = '#8fd8ff';

/**
 * The footwork keys, in the duellist's own frame: forward is toward the
 * opponent, strafe is toward their own right. Keyed by `code` rather than
 * `key`, so the physical WASD block works on a layout that does not spell WASD.
 */
const MOVE_KEYS: Record<string, { forward: number; strafe: number }> = {
  KeyW: { forward: 1, strafe: 0 },
  KeyS: { forward: -1, strafe: 0 },
  KeyA: { forward: 0, strafe: -1 },
  KeyD: { forward: 0, strafe: 1 },
  ArrowUp: { forward: 1, strafe: 0 },
  ArrowDown: { forward: -1, strafe: 0 },
  ArrowLeft: { forward: 0, strafe: -1 },
  ArrowRight: { forward: 0, strafe: 1 },
};

/** Sum the held keys into one intent, so opposite keys cancel rather than fight. */
function intentFrom(held: Set<string>): { forward: number; strafe: number } {
  let forward = 0;
  let strafe = 0;
  for (const code of held) {
    const key = MOVE_KEYS[code];
    if (!key) continue;
    forward += key.forward;
    strafe += key.strafe;
  }
  return {
    forward: Math.max(-1, Math.min(1, forward)),
    strafe: Math.max(-1, Math.min(1, strafe)),
  };
}

export interface CombatInputHandle {
  requestLock: () => void;
  beginCast: () => void;
  endCast: () => void;
}

export function useCombatInput(canvas: HTMLCanvasElement | null, enabled: boolean): CombatInputHandle {
  const handle = useRef<CombatInputHandle>({
    requestLock: () => {},
    beginCast: () => {},
    endCast: () => {},
  });
  const lastAimSent = useRef(0);
  const casting = useRef(false);
  /** Which movement keys are physically down. Read by the prediction loop. */
  const held = useRef(new Set<string>());

  useEffect(() => {
    if (!enabled || !canvas) return;

    const sendAim = (force = false) => {
      const now = performance.now();
      if (!force && now - lastAimSent.current < AIM_SEND_INTERVAL_MS) return;
      lastAimSent.current = now;
      net.send(C2S.AIM, { yaw: bus.yaw, pitch: bus.pitch });
    };

    const requestLock = () => {
      if (document.pointerLockElement !== canvas) {
        void canvas.requestPointerLock();
      }
      sfx.unlock();
    };

    /**
     * A cast no longer needs to know what it is.
     *
     * Nothing is selected in advance: the circle opens in a neutral arcane
     * colour and takes on the identity of whichever spell the live guess
     * settles on as the sigil is drawn. The guess is a display decision only —
     * `endCast` ships the raw evidence and the server names the spell.
     */
    const beginCast = () => {
      if (casting.current) return;
      const state = useNet.getState();
      if (state.phase !== 'round') return;
      if (state.slots.length === 0) return;

      casting.current = true;
      resetCast();
      castState.active = true;
      castState.startedAt = SpellNet.now();
      castState.color = NEUTRAL_CAST_COLOR;
      // With no microphone the key-down *is* the utterance start; that is the
      // honest timestamp for a typed chant and keeps the window symmetric.
      castState.onsetAt = useCastUi.getState().usingFallbackInput ? castState.startedAt : 0;

      bus.casting = true;
      bus.castColor = NEUTRAL_CAST_COLOR;
      bus.circle.active = true;
      bus.circle.progress = 0;
      bus.circle.color = NEUTRAL_CAST_COLOR;
      bus.circle.accent = '#ffffff';
      bus.circle.complexity = 2;

      gestureTrail.color = NEUTRAL_CAST_COLOR;
      combatRecorder.begin();
      useCastUi.getState().setCasting(true);
      sfx.circle();
      net.send(C2S.CASTING, { active: true, progress: 0, color: NEUTRAL_CAST_COLOR });
    };

    const endCast = () => {
      if (!casting.current) return;
      casting.current = false;

      const state = useNet.getState();
      const points = combatRecorder.finish();
      // Prefer what was actually heard; fall back to the typed chant. A
      // recogniser that dies mid-match must not silently disarm the player.
      const typed = useCastUi.getState().typedChant;
      const transcript = castState.transcript.trim() || typed;
      // Snapshot before `resetCast` wipes it: the verdict arrives later, and it
      // is the verdict that has to show the player what they actually said.
      // "Expected" is the guess's chant — the closest thing the client knows to
      // what the player was aiming for, and the server's own answer replaces it
      // in the readout as soon as it lands.
      lastAttempt.heard = transcript;
      lastAttempt.expected = state.ownSpells[castState.guessId]?.normalizedChant ?? '';

      bus.casting = false;
      bus.circle.active = false;
      gestureTrail.active = false;
      useCastUi.getState().setCasting(false);
      net.send(C2S.CASTING, { active: false, progress: 0, color: bus.castColor });

      net.send(C2S.CAST, {
        transcript,
        gesture: points,
        // Fall back to the key-down moment if the microphone never fired: a
        // silent cast should still be judged, just without the timing edge.
        speechStartAt: castState.onsetAt || castState.startedAt,
        yaw: bus.yaw,
        pitch: bus.pitch,
      });
      wandRig.kick(0.8);
      sfx.signature(1);
      resetCast();
    };

    /**
     * Lift the pen mid-cast. A registered sigil can have several strokes, and
     * without this the drawing in combat would be one unbroken line — the
     * connecting sweep between strokes would be scored as part of the shape.
     */
    const breakStroke = () => {
      if (!casting.current) return;
      combatRecorder.endStroke();
      syncTrailFrom(combatRecorder);
      sfx.ui();
    };

    handle.current = { requestLock, beginCast, endCast };

    /* --- listeners ---------------------------------------------------- */

    const onMouseMove = (event: MouseEvent) => {
      if (document.pointerLockElement !== canvas) return;
      const { sensitivity, invertY } = useSettings.getState();
      if (casting.current) {
        // Camera is pinned; the pointer is drawing.
        combatRecorder.addDelta(event.movementX, event.movementY);
        syncTrailFrom(combatRecorder);
        return;
      }
      const scale = 0.0022 * sensitivity;
      bus.yaw += event.movementX * scale;
      bus.pitch += event.movementY * scale * (invertY ? 1 : -1);
      // Fixed stance: a duellist can look around but not turn their back.
      bus.yaw = Math.max(-1.15, Math.min(1.15, bus.yaw));
      bus.pitch = Math.max(-0.62, Math.min(0.62, bus.pitch));
      sendAim();
    };

    /**
     * Which input opened the current cast, so only that same input can end it.
     *
     * With the free bolt gone the left button had nothing left to do, so it
     * became a third way to hold a cast — mouse or keyboard, it is now one
     * verb. Tracking the holder matters because the inputs overlap: left-click
     * also lifts the pen between strokes, and releasing Space must not cancel a
     * cast the mouse is holding. The rule is simply that whatever started the
     * cast finishes it, and everything else keeps its other job. Shift always
     * breaks a stroke, which is the only way to do it during a left-hand cast.
     */
    const castHolder = { current: '' };
    const holdCast = (id: string) => {
      if (casting.current) return false;
      castHolder.current = id;
      beginCast();
      // `beginCast` refuses outside a round or with an empty book; do not leave
      // a holder behind for a cast that never started.
      if (!casting.current) castHolder.current = '';
      return true;
    };
    const releaseCast = (id: string) => {
      if (castHolder.current !== id) return;
      castHolder.current = '';
      endCast();
    };

    const onMouseDown = (event: MouseEvent) => {
      if (document.pointerLockElement !== canvas) {
        requestLock();
        return;
      }
      if (event.button !== 0 && event.button !== 2) return;
      const id = `mouse${event.button}`;
      if (!casting.current) holdCast(id);
      else if (castHolder.current !== id) breakStroke();
    };

    const onMouseUp = (event: MouseEvent) => releaseCast(`mouse${event.button}`);

    const onContextMenu = (event: MouseEvent) => event.preventDefault();

    const onKeyDown = (event: KeyboardEvent) => {
      const target = event.target as HTMLElement | null;
      if (target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA')) return;

      if (MOVE_KEYS[event.code]) {
        // Not gated on `event.repeat`: the auto-repeat re-asserts a key that a
        // dropped keyup would otherwise have left stuck down.
        event.preventDefault();
        held.current.add(event.code);
        return;
      }
      if (event.repeat) return;

      if (event.code === 'ShiftLeft' || event.code === 'ShiftRight') {
        breakStroke();
      } else if (event.code === 'Space') {
        event.preventDefault();
        holdCast('Space');
      } else if (event.code === 'Escape') {
        document.exitPointerLock?.();
      }
    };

    const onKeyUp = (event: KeyboardEvent) => {
      if (MOVE_KEYS[event.code]) {
        held.current.delete(event.code);
        return;
      }
      if (event.code === 'Space') {
        event.preventDefault();
        releaseCast(event.code);
      }
    };

    /**
     * Anything that takes the keyboard away drops every held key.
     *
     * A keyup that arrives while the window is not focused never arrives at
     * all, and the server keeps walking on the last direction it was told — so
     * a duellist who alt-tabbed mid-step would come back pressed against the
     * edge of their box. The loop below notices the empty set and sends a stop.
     */
    const releaseAllKeys = () => held.current.clear();

    const onPointerLockChange = () => {
      if (document.pointerLockElement !== canvas) {
        releaseAllKeys();
        if (casting.current) {
          castHolder.current = '';
          endCast();
        }
      }
    };

    // Speech onset: the moment the player actually makes noise.
    //
    // The *most recent* onset inside the cast wins, not the first. A throat
    // clear, a stray word, or room noise at the start of a long hold would
    // otherwise date the parry to that instead of to the incantation itself.
    const offOnset = mic.onOnset((at) => {
      if (!casting.current) return;
      castState.onsetAt = at;
    });

    const offRecognition = chantRecognizer.onUpdate(({ transcript, isFinal }) => {
      if (!casting.current) return;
      castState.transcript = transcript;
      castState.finalized = isFinal;
      // Progress against the *guessed* spell's chant drives the circle build-up
      // and the rune telegraph the opponent sees. Re-read every update, because
      // a few more words can change which spell the guess has settled on.
      const spell = useNet.getState().ownSpells[castState.guessId];
      if (spell) {
        const spoken = normalizeChant(transcript);
        const wanted = Math.max(1, chantSyllables(spell.normalizedChant));
        const said = chantSyllables(spoken);
        const similarity = chantSimilarity(spoken, spell.normalizedChant);
        castState.progress = Math.max(
          Math.min(1, said / wanted),
          similarity,
        );
      }
    });

    canvas.addEventListener('mousedown', onMouseDown);
    canvas.addEventListener('contextmenu', onContextMenu);
    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onMouseUp);
    window.addEventListener('keydown', onKeyDown);
    window.addEventListener('keyup', onKeyUp);
    window.addEventListener('blur', releaseAllKeys);
    document.addEventListener('visibilitychange', releaseAllKeys);
    document.addEventListener('pointerlockchange', onPointerLockChange);

    return () => {
      canvas.removeEventListener('mousedown', onMouseDown);
      canvas.removeEventListener('contextmenu', onContextMenu);
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUp);
      window.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('keyup', onKeyUp);
      window.removeEventListener('blur', releaseAllKeys);
      document.removeEventListener('visibilitychange', releaseAllKeys);
      document.removeEventListener('pointerlockchange', onPointerLockChange);
      releaseAllKeys();
      offOnset();
      offRecognition();
    };
  }, [canvas, enabled]);

  /* --- footwork + live guess + circle build-up + telegraph relay ------- */
  useEffect(() => {
    if (!enabled) return;
    let raf = 0;
    let lastSent = 0;
    let lastGuessAt = 0;
    let lastFrameAt = performance.now();
    let lastMoveSentAt = 0;
    let sentForward = 0;
    let sentStrafe = 0;

    /**
     * One tick of footwork: predict, reconcile, then tell the server which way
     * we are holding.
     *
     * The prediction runs the *same* `stepStance` the room runs, from the same
     * rule package, which is the only reason it is allowed to exist — a second
     * implementation of the walk would drift and the correction below would
     * read as the camera fighting the player.
     */
    const stepFootwork = (delta: number) => {
      const intent = intentFrom(held.current);
      // Every condition the room refuses to walk under, refused here too. If
      // these two disagree, the reconciliation turns into a yank.
      const pinned =
        castState.active ||
        bus.selfStunned ||
        useNet.getState().phase !== 'round';
      const walking = !pinned && (intent.forward !== 0 || intent.strafe !== 0);

      if (walking) {
        const next = stepStance(
          bus.selfSide,
          { x: bus.selfX, z: bus.selfZ },
          intent.forward,
          intent.strafe,
          stanceSpeed(bus.selfSlowed),
          delta,
        );
        bus.selfX = next.x;
        bus.selfZ = next.z;
      }

      // Ease onto the server's answer. Small disagreements are walked off over
      // a few frames; a large one means the prediction was wrong about
      // something structural — a stun, a round reset — and is taken whole.
      const dx = bus.serverSelfX - bus.selfX;
      const dz = bus.serverSelfZ - bus.selfZ;
      if (Math.hypot(dx, dz) > MOVEMENT.RECONCILE_SNAP_M) {
        bus.selfX = bus.serverSelfX;
        bus.selfZ = bus.serverSelfZ;
      } else {
        const k = Math.min(1, delta * MOVEMENT.RECONCILE_RATE);
        bus.selfX += dx * k;
        bus.selfZ += dz * k;
      }

      // A pinned duellist is standing still whatever the keyboard says, and the
      // server must hear that rather than keep integrating a stale direction.
      const forward = pinned ? 0 : intent.forward;
      const strafe = pinned ? 0 : intent.strafe;
      const now = performance.now();
      const changed = forward !== sentForward || strafe !== sentStrafe;
      const stale = (forward !== 0 || strafe !== 0)
        && now - lastMoveSentAt > MOVEMENT.INPUT_INTERVAL_MS;
      if (!changed && !stale) return;
      sentForward = forward;
      sentStrafe = strafe;
      lastMoveSentAt = now;
      net.send(C2S.MOVE, { forward, strafe });
    };

    const loop = () => {
      const frameAt = performance.now();
      // Capped so a backgrounded tab does not resume with one enormous stride.
      const delta = Math.min(0.05, (frameAt - lastFrameAt) / 1000);
      lastFrameAt = frameAt;
      stepFootwork(delta);

      if (castState.active) {
        const now = performance.now();
        // Re-guess a few times a second rather than every frame: scoring the
        // whole book against a growing stroke is the most expensive thing in
        // this loop, and the answer cannot visibly change faster than this.
        if (now - lastGuessAt > 110) {
          lastGuessAt = now;
          updateGuess();
        }

        const elapsed = (SpellNet.now() - castState.startedAt) / 900;
        bus.circle.progress = Math.max(castState.progress, Math.min(1, elapsed));
        if (now - lastSent > 120) {
          lastSent = now;
          net.send(C2S.CASTING, {
            active: true,
            progress: bus.circle.progress,
            color: bus.castColor,
          });
        }
      }
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(raf);
  }, [enabled]);

  return handle.current;
}

/**
 * The client's running answer to "what am I casting?", for the visuals only.
 *
 * It runs the *same* two scorers the server will run — `scoreAttempt` against
 * each registered template and `chantSimilarity` against each chant — over the
 * stroke drawn so far. That matters: a preview computed a different way would
 * drift from the verdict and teach the player to trust the wrong thing. What it
 * deliberately does not do is decide anything. It writes a colour, a rune count
 * and a highlighted row, and the server re-derives the answer from the evidence
 * when the key is released.
 */
function updateGuess() {
  const state = useNet.getState();
  const points = combatRecorder.points;
  const spoken = normalizeChant(
    castState.transcript.trim() || useCastUi.getState().typedChant,
  );
  // Nothing to go on yet — a two-point scribble matches everything equally.
  if (points.length < 8 && !spoken) return;

  let bestId = '';
  let bestScore = -1;
  for (const id of state.slots) {
    const spell = state.ownSpells[id];
    if (!spell || spell.type === 'basic') continue;
    const gesture = spell.gesture && points.length >= 8
      ? scoreAttempt(points, spell.gesture).score
      : 0;
    const chant = spoken ? chantSimilarity(spoken, spell.normalizedChant) : 0;
    const combined = combineScores(gesture, chant).combined;
    if (combined > bestScore) {
      bestScore = combined;
      bestId = id;
    }
  }

  castState.guessScore = Math.max(0, bestScore);
  if (!bestId || bestId === castState.guessId) return;

  castState.guessId = bestId;
  const spell = state.ownSpells[bestId];
  if (!spell) return;
  castState.color = spell.visuals.color;
  bus.castColor = spell.visuals.color;
  bus.circle.color = spell.visuals.color;
  bus.circle.accent = spell.visuals.accentColor;
  bus.circle.complexity = spell.visuals.circleComplexity;
  gestureTrail.color = spell.visuals.color;
}
